export interface AcknowledgeNotification {
    notificationId: string
}

export interface GetNotifications {
    employeeId: string
}

export interface GetSupportRequests {
    employeeId: string
}

export interface NotificationObject {
    id: string,
    applicationId: string,
    userId: string,
    userFirstName?: string,
    userLastName?: string,
    userEmail?: string,
    country: string,
    adminAcknowledged: boolean,
    notificationType: string,
    source: string,
    isError: boolean,
    notificationMessage: string,
    tsCreatedAt: number,
    _rid: string,
    _self: string,
    _etag: string,
    _attachments: string,
    _ts: number
}

export interface SupportRequestObject {
    id: string,
    requesterId: string,
    applicationId: string,
    name: string,
    email: string,
    country: string,
    createdAt: number,
    title: string,
    message: string,
    isOpen: boolean,
    resolution: string
}

export const init_NotificationObject: NotificationObject = {
    id: "",
    applicationId: "",
    userId: "",
    country: "",
    adminAcknowledged: false,
    notificationType: "",
    source: "",
    isError: false,
    notificationMessage: "",
    tsCreatedAt: 0,
    _rid: "",
    _self: "",
    _etag: "",
    _attachments: "",
    _ts: 0
}

export enum EventTypes {
    USER_CREATED_EVENT = "User created",
    USER_UPDATED_EVENT = "User updated",
    USER_AUTOAPPROVED_TO_APPLICTATION_EVENT = "User autoapproved to application",
    USER_MANUALLY_APPROVED_TO_APPLICATION_EVENT = "User manually approved to application",
    USER_MANUALLY_REJECTED_FROM_APPLICATION_EVENT = "User manually rejected from application",
    EMAIL_SENT_EVENT = "Email sent to user.",
    SIGN_IN = "Signin Completed",
    ENDPOINT_TRIGGERED = "Endpoint triggered event"
}

export interface WriteLogRequestBody {
    country: string,
    message: string,
    tags?: Array<string>,
    initiatedBy: string,
    targetApplication?: string,
    affectedUser: string,
    eventType: EventTypes,
    result: "Success" | "Failure"
}

export interface SearchLogRequestBody {
    before?: number,
    after?: number,
    initiatedBy?: string,
    targetApplication?: string,
    affectedUser: string,
    eventType: EventTypes,
    count: number,
    offset: number
}

export interface SearchLogResponse {
    id: string,
    createdAt: number,
    country: string,
    message: string,
    tags?: Array<string>,
    initiatedBy?: string,
    targetApplication?: string,
    affectedUser: string,
    eventType: EventTypes,
    result: "Success" | "Failure",
    failureResponse?: string,
    failureStackTrace?: string
}

export interface AllUserApplicationRequests {
    id: string,
    users: Array<UserApplicationRequests>
}

export interface UserApplicationRequests {
    requestId: string,
    id: string,
    country: string,
    email: string,
    firstName: string,
    lastName: string
}

export interface UserGroupRequests {
  id: string,
  groupId: string,
  userId: string,
  userCountry: string | null,
  status: "Pending" | "Approved" | "Rejected",
  history: Array<any>,
  userEmail: string,
  userFirstName: string,
  userLastName: string
}

export interface Organization {
    id: string,
    name: string,
    appList: Array<string>,
    claims: Array<Claims>,
    [ORG_KEY: string]: any
}

export interface ExternalEnumRecord {
    byValue: Record<string, ClaimValue>,
    values: Array<ClaimValue>
}

export const makeExternalEnumRecord = (enumerations: Record<string, Array<ClaimValue>>): Record<string, ExternalEnumRecord> => {
    return Object.keys(enumerations).reduce((r: Record<string, ExternalEnumRecord>, currentEnum) => {
        r[currentEnum] = {
            values: enumerations[currentEnum],
            byValue: enumerations[currentEnum].reduce((res: Record<string, ClaimValue>, currentVal) => {
                res[currentVal.value] = currentVal;
                return res;
            }, {})
        }
        return r;
    }, {});
}

export type ExternalEnumerationFileType = 'CSV' | 'XLSX';

export interface ExternalEnumerationFormat {
    fileType: ExternalEnumerationFileType;
    friendlyNameColumnHeader?: string;
    valueColumnHeader?: string;
    friendlyNameColumnIndex?: number;
    valueColumnIndex?: number;
    separator?: string;
    sheetId?: string;
    useFriendlyNames: boolean;
    hasHeaderRow: boolean;
    skipMalformedLines: boolean;
}

export interface ExternalEnumerationReference {
    url: string;
    format: ExternalEnumerationFormat;
}

export interface Claims {
        id: string,
    name: string,
        userInputType : string,
    possibleValues?: Array<ClaimValue>,
    dataType: string,
    isBaseClaim: boolean,
    required: boolean,
    valuesDependOn: string,
    allowNullForEmptyDependencySet: boolean,
    requiredForEachDependencySet: boolean,
    readOnly: boolean,
    hideOnCreate: boolean,
    hidden: boolean,
    enabledOn?: Array<ClaimReference>,
    requiredOn?: Array<ClaimReference>,
    externalEnumeration: ExternalEnumerationReference
}

export interface ClaimReference {
    id: string,
    value: string
}

export interface ClaimValue {
    name: string,
    value: string,
    enabledFor?: Array<string>
}

export const claimsListToSchema = (claimDefs: Array<Claims>) => {
    return claimDefs.reduce((sd: Record<string, Claims>, nxtClaim) => {
        sd[nxtClaim.id] = nxtClaim;
        return sd;
    }, {});
}

export const claimsValidInContext = (claimDef: Claims, allClaimDefs: Record<string, Claims>, claimEnum: Array<ClaimValue>, claimContext: Record<string, string>) => {
    return claimEnum.filter(claimVal => (!claimDef.valuesDependOn) || ((!!claimContext[claimDef.valuesDependOn]) && (claimVal.enabledFor && (
        (allClaimDefs[claimDef.valuesDependOn].userInputType == "CheckboxMultiSelect") ?
            (claimContext[claimDef.valuesDependOn]?.split(",") ?? []).some(dependsOnVal => claimVal.enabledFor?.includes(dependsOnVal)) :    
            claimVal.enabledFor.includes(claimContext[claimDef.valuesDependOn]))))
    );
}

export const claimEnabledInContext = (claimDef: Claims, allClaimDefs: Record<string, Claims>, claimEnum: Array<ClaimValue> | undefined, claimContext: Record<string, string>) => {
    return (!claimDef.hidden) && (((claimDef.enabledOn == null) || claimDef.enabledOn.some(enableClaim => {
        if (allClaimDefs[enableClaim.id].userInputType == "CheckboxMultiSelect") {
            return (claimContext?.[enableClaim.id]?.split(",") ?? []).some(enableClaimVal => enableClaimVal == enableClaim.value)
        } else {
            return (claimContext?.[enableClaim.id] == enableClaim.value)
        }
    })) && ((!claimDef.allowNullForEmptyDependencySet) || (!claimDef.valuesDependOn) || (claimEnum && (claimsValidInContext(claimDef, allClaimDefs, claimEnum, claimContext)?.length > 0))));
}

export const claimRequiredInContext = (claimDef: Claims, allClaimDefs: Record<string, Claims>, claimEnum: Array<ClaimValue> | undefined, claimContext: Record<string, string>) => {
    return claimEnabledInContext(claimDef, allClaimDefs, claimEnum, claimContext) && (claimDef.required || ((claimDef.requiredOn != null) && claimDef.requiredOn.some(requireClaim => {
        if (allClaimDefs[requireClaim.id].userInputType == "CheckboxMultiSelect") {
            return (claimContext?.[requireClaim.id]?.split(",") ?? []).some(requireClaimVal => requireClaimVal == requireClaim.value);
        } else {
            return claimContext?.[requireClaim.id] == requireClaim.value;
        }
    })));
}

export const claimValueValidInContext = (claimDef: Claims, allClaimDefs: Record<string, Claims>, claimEnum: Array<ClaimValue>, claimContext: Record<string, string>) => {
    let valsToValidate: Array<string> = [];
    if (claimDef.userInputType === "CheckboxMultiSelect") {
        valsToValidate = claimContext[claimDef.id]?.split(",");
    } else {
        valsToValidate = [claimContext[claimDef.id]]
    }
    let validOptions = claimsValidInContext(claimDef, allClaimDefs, claimEnum, claimContext);
    let valid = valsToValidate.every(claimVal => validOptions.some(validClaimVal => validClaimVal.value === claimVal));
    if (valid && claimDef.required && claimDef.valuesDependOn && claimContext[claimDef.valuesDependOn] && claimDef.requiredForEachDependencySet && (allClaimDefs[claimDef.valuesDependOn]?.userInputType === "CheckboxMultiSelect")) {
        let parentVals = claimContext[claimDef.valuesDependOn]?.split(",") ?? [];
        valid = parentVals.every(parentVal => claimEnum.filter(cVal => cVal.enabledFor?.includes(parentVal)).some(pVal => valsToValidate.includes(pVal.value)));
    }
    return valid;
}

export interface ApplicationData {
    id: string,
    name: string,
    applicationCategory: string,
    claims: Array<Claims>,
    requiresAdminApproval: boolean,
    allowedUserTypes?: Array<string>,
    adminsCannotSetProvisioningStatus?: boolean
}

interface GroupMemberInfo {
  id: string;
  email: string;
  firstName?: string
  lastName?: string
}

export interface Group{
  id: string,
  displayName: string,
  description: string,
  owners: Array<{id: string, type: number}>,
  adminWritable: boolean,
  customMetadata: any,
  applications: Array<any>,
  members: Array<GroupMemberInfo>
}

